import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  SearchBar,
  TableActionTypes,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import {
  AdminDetailsResponseContentObject,
  UserGroupDetailsResponse,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { usePostAdminDetails } from '../../../api-refactored/user-group-access-configuration/usePostAdminDetails';

const useStyles = makeStyles()((theme) =>
({
  dialog: {
    overflow: 'hidden',
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  userNameText: {
    color: theme.color.text.textTertiary,
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  errorMessage: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
}));

const tableColumns = (
  data: AdminDetailsResponseContentObject[],
  t: TFunction<'translation'>,
  classes: Record<string,string>
): MUIDataTableColumnDef[] => [
    {
      name: 'adminName',
      label: t('admin->>UserGroups.AdminName'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">{data[dataIndex].name}</Typography>
            <Typography className={classes.userNameText} variant="subtitle2">
              {data[dataIndex].username}
            </Typography>
          </>
        ),
      },
    },
    {
      name: 'email',
      label: t('admin->>UserGroups.Email'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{data[dataIndex].email}</Typography>
        ),
      },
    },
    {
      name: 'accountStatus',
      label: t('admin->>UserGroups.AccountStatus'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Pill
            variant={data[dataIndex].isEnabled ? 'positive' : 'negative'}
            label={data[dataIndex].isEnabled ? 'Active' : 'Not Activated'}
          />
        ),
      },
    },
  ];

interface AddAdminModalProps {
  modalState: boolean;
  setOpenModalState: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAdminUuids: string[];
  setSelectedAdminUuids: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedAdminDetails: React.Dispatch<
    React.SetStateAction<AdminDetailsResponseContentObject[]>
  >;
  userGroupDetails?: UserGroupDetailsResponse;
}

const AddAdminModal = ({
  modalState,
  setOpenModalState,
  selectedAdminUuids,
  setSelectedAdminUuids,
  setSelectedAdminDetails,
  userGroupDetails,
}: AddAdminModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();

  const [errorMessage, setErrorMessage] = useState<string>('');

  const { state, dispatch: tableDispatch, tOptions, remoteTotalRowsSelected } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'adminName',
    order: 'asc',
    size: 5,
    page: 0,
    search: '',
  });

  const { mutate: mutatePostAdminDetails } = usePostAdminDetails({
    onSuccess: (res) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.content,
          count: res.totalElements,
        },
      } as TableActionTypes);
    },
    onError: () => {
      tableDispatch({ type: 'CLEAR_SELECTION' });
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: TABLE_INITIAL_STATE,
      } as TableActionTypes);
    },
  });

  useEffect(() => {
    if (modalState) {
      mutatePostAdminDetails({
        selectedAdminUuids: selectedAdminUuids.length === 0 ? [''] : selectedAdminUuids,
        q: state.search || '',
        apiPathParam: {
          page: state.page || 0,
          pageSize: state.size || 5,
        },
      });
    }
  }, [
    modalState,
    mutatePostAdminDetails,
    selectedAdminUuids,
    state.page,
    state.search,
    state.size,
  ]);

  useEffect(() => {
    if (remoteTotalRowsSelected) {
      setErrorMessage('');
    }
  }, [remoteTotalRowsSelected]);

  const handleCloseAddAdminModal = () => {
    tableDispatch({ type: 'CLEAR_SELECTION' });
    setOpenModalState(false);
  };

  const handleAddAdmin = () => {
    if (remoteTotalRowsSelected) {
      setSelectedAdminUuids((previousSelectedAdminUuids) =>
        previousSelectedAdminUuids.concat(
          Object.values(state.rowsSelected).map((row) => row.userUuid)
        )
      );
      setSelectedAdminDetails((previousSelectedAdminDetails) =>
        previousSelectedAdminDetails.concat(Object.values(state.rowsSelected))
      );
      handleCloseAddAdminModal();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserGroups.UsersAddedSnackbarMessage'),
      });
    }
  };

  return (
    <>
      <Dialog
        id="user-group-access-configuration-add-admin-dialog"
        fullWidth
        open={modalState}
        maxWidth="md"
        slotProps={{
          paper: {className: classes.dialog}
        }}
      >
        <DialogTitle>{t('admin->>UserGroups.AddAdmin')}</DialogTitle>
        <DialogContent>
          <Box className={classes.searchContainer}>
            <SearchBar
              placeholder={t('admin->>UserGroups.AddAdminSearchPlaceholder')}
              name="searchInputAddAdmin"
              ariaLabel="searchInputAddAdmin"
              defaultValue={state.search}
              onSearch={(searchStr: string) => {
                tableDispatch({ type: 'CLEAR_SELECTION' });
                tableDispatch({
                  type: 'UPDATE_SEARCH',
                  payload: searchStr.trim(),
                });
              }}
            />
          </Box>

          <Box>
            <MUIDataTable
              state={state}
              dispatch={tableDispatch}
              columns={tableColumns(state.data, t, classes)}
              data={state.data}
              emptyDataLabel={t('admin->>UserGroups.NoAdminMessage')}
              options={{
                ...tOptions,
                pagination: true,
                sort: true,
                selectToolbarPlacement: 'none',
                selectableRows: 'multiple',
              }}
              rowsPerPageOptions={[5, 10, 20, 40, 50]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box className={classes.buttonContainer}>
            {errorMessage && (
              <Typography className={classes.errorMessage} variant="body2" color="error">
                {errorMessage}
              </Typography>
            )}
            <Button
              id="button-cancel-add-admin"
              type="button"
              variant="text"
              onClick={handleCloseAddAdminModal}
            >
              {t('admin->>UserGroups.Cancel')}
            </Button>
            <Button id="button-add-admin" type="submit" onClick={handleAddAdmin}>
              {t('admin->>UserGroups.AddAdminCount', {
                adminCount: remoteTotalRowsSelected,
              })}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddAdminModal;
